import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink, CheckCircle } from "lucide-react";

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("visible");
        });
      },
      { threshold: 0.1 }
    );
    const el = ref.current;
    if (el) {
      el.querySelectorAll(".animate-on-scroll").forEach((c) => observer.observe(c));
      if (el.classList.contains("animate-on-scroll")) observer.observe(el);
    }
    return () => observer.disconnect();
  }, []);
  return ref;
}

const brands = [
  {
    name: "Dahua",
    desc: "Leading global provider of video surveillance solutions with AI-powered analytics and 4K technology.",
    color: "#0066CC",
    products: ["CCTV Cameras", "DVR/NVR Systems", "Video Analytics", "AI Solutions"],
  },
  {
    name: "Hikvision",
    desc: "World's largest manufacturer of video surveillance products with cutting-edge AI technology.",
    color: "#E83000",
    products: ["IP Cameras", "Turbo HD Systems", "Access Control", "Alarm Systems"],
  },
  {
    name: "EZVIZ",
    desc: "Smart home security brand offering easy-to-use cameras and smart home devices.",
    color: "#FF6B00",
    products: ["WiFi Cameras", "Smart Home Devices", "Video Doorbells", "Sensors"],
  },
  {
    name: "TP-Link",
    desc: "Global provider of networking products for home, business, and ISP solutions.",
    color: "#4CAF50",
    products: ["Routers", "Switches", "Access Points", "Network Adapters"],
  },
  {
    name: "Ruijie",
    desc: "Enterprise networking solutions with cloud-managed infrastructure for businesses.",
    color: "#1565C0",
    products: ["Enterprise WiFi", "Cloud Management", "Switches", "Gateways"],
  },
  {
    name: "Ubiquiti",
    desc: "High-performance networking technology for service providers and enterprises.",
    color: "#1A1A2E",
    products: ["UniFi Systems", "EdgeRouters", "AirMAX", "Fiber Solutions"],
  },
  {
    name: "Starlink",
    desc: "SpaceX satellite internet constellation providing high-speed broadband globally.",
    color: "#00A6FF",
    products: ["Residential Internet", "Business Plans", "Maritime Solutions", "RV Plans"],
  },
];

export default function Brands() {
  const ref = useInView();

  return (
    <div ref={ref}>
      {/* Hero */}
      <section className="pt-32 pb-20 section-padding bg-brand-gray">
        <div className="container-custom">
          <div className="max-w-3xl">
            <p className="animate-on-scroll text-sm font-semibold text-brand-orange uppercase tracking-wider mb-3">Our Partners</p>
            <h1 className="animate-on-scroll stagger-1 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              Trusted <span className="gradient-text">Global Brands</span>
            </h1>
            <p className="animate-on-scroll stagger-2 text-lg text-brand-muted leading-relaxed">
              We partner with world-renowned brands to deliver the best security and technology solutions in Sri Lanka.
            </p>
          </div>
        </div>
      </section>

      {/* Brands Grid */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {brands.map((b, i) => (
              <div key={i} className={`animate-on-scroll stagger-${(i % 4) + 1} bg-white rounded-3xl p-8 border border-brand-border/50 shadow-card overflow-hidden hover:shadow-card-hover transition-all duration-500 hover:-translate-y-1`}>
                <div className="flex items-start gap-5 min-w-0">
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center shrink-0 text-white font-bold text-xl"
                    style={{ backgroundColor: b.color }}
                  >
                    {b.name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-display text-xl font-bold mb-2" style={{ color: b.color }}>{b.name}</h3>
                    <p className="text-sm text-brand-muted leading-relaxed mb-4">{b.desc}</p>
                    <div className="flex flex-wrap gap-2">
                      {b.products.map((p, j) => (
                        <span key={j} className="inline-flex items-center gap-1 px-3 py-1 bg-brand-gray rounded-lg text-xs font-medium text-brand-dark/70">
                          <CheckCircle size={10} className="text-brand-orange" />
                          {p}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-brand-gray">
        <div className="container-custom text-center">
          <h2 className="animate-on-scroll font-display text-3xl sm:text-4xl font-bold tracking-tight mb-6">
            Authorized Dealer
          </h2>
          <p className="animate-on-scroll stagger-1 text-lg text-brand-muted mb-10 max-w-2xl mx-auto">
            As an authorized dealer, we provide genuine products with full manufacturer warranty and local support.
          </p>
          <div className="animate-on-scroll stagger-2 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary">
              Get a Quote <ArrowRight size={18} className="ml-2" />
            </Link>
            <Link to="/products" className="btn-outline">
              View Products
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
